import type {
  SubscriptionEvent,
  ArcheHandoffPayload,
  BridgeMessage,
  BridgeResponse,
  BridgeInitMessage,
  PluginToHostMessageType,
  FiasTheme,
  FiasVendoredAssets,
  FiasTextModel,
  FiasHolder,
  FiasAIActionsDeclaration,
  FiasChromeNavigateTarget,
  FiasChromeSetPayload,
  FiasChromeState,
  PluginPermission,
  EntityInvocationResult,
  SubscriptionEndedReason,
} from './types';
import { getDefaultTheme } from './theme';
import { SDK_BRIDGE_PROTOCOL_VERSION, SDK_REQUIRES_HOST_PROTOCOL_VERSION } from './protocol';
import { installSandboxDialogShims } from './sandbox-dialogs';
import { checkInvocationRequestSize } from './invocation-params';

let messageCounter = 0;

function generateMessageId(): string {
  return `msg_${Date.now()}_${++messageCounter}`;
}

/**
 * Error type for every rejection the bridge produces.
 *
 * Plugins routinely `JSON.stringify` a caught error to log or display it —
 * and `JSON.stringify(new Error(...))` yields `{}` because Error's props are
 * non-enumerable. That's the Rota-incident failure shape: an entity_invoke
 * rejection reached plugin code as an opaque `{}` and the plugin crashed on
 * the missing result. `toJSON()` makes stringification meaningful, and the
 * machine-readable `code` lets plugin code branch on failure kinds
 * (`TIMEOUT`, `BRIDGE_DESTROYED`, server codes like `BRIDGE_TOKEN_EXPIRED`).
 *
 * WIRE-FORMAT NOTE: the host posts plain fields (`{ error: string,
 * errorCode?: string }`) in the response message; the SDK constructs this
 * class locally. Never rely on structured-cloning an Error across the
 * postMessage boundary.
 */
export class FiasBridgeError extends Error {
  code: string;
  /**
   * The platform's correlation id for the failed call, when the platform sent
   * one. Show it as a support reference — it is the id on the platform's own
   * log line for this failure.
   */
  requestId?: string;

  constructor(message: string, code: string, requestId?: string) {
    super(message);
    this.name = 'FiasBridgeError';
    this.code = code;
    if (requestId) this.requestId = requestId;
  }

  toJSON(): { name: string; code: string; message: string; requestId?: string } {
    return {
      name: 'FiasBridgeError',
      code: this.code,
      message: this.message,
      ...(this.requestId ? { requestId: this.requestId } : {}),
    };
  }
}

/**
 * Merges an incoming theme with defaults to ensure all fields are present.
 * Protects plugins from crashing when the host sends an incomplete theme.
 */
function mergeThemeWithDefaults(incoming: Partial<FiasTheme>): FiasTheme {
  const defaults = getDefaultTheme(incoming.mode ?? 'dark');
  return {
    ...defaults,
    ...incoming,
    colors: { ...defaults.colors, ...(incoming.colors ?? {}) },
    spacing: { ...defaults.spacing, ...(incoming.spacing ?? {}) },
    fonts: { ...defaults.fonts, ...(incoming.fonts ?? {}) },
    components: { ...defaults.components, ...(incoming.components ?? {}) },
  };
}

type PendingRequest = {
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
  onToken?: (text: string) => void;
  onThinking?: (text: string) => void;
  /** Streaming only: restarts the idle timer when the stream shows life. */
  onActivity?: () => void;
  /** Streaming only: clears the stream's extra timers and abort listener. */
  cleanup?: () => void;
};

/** Callbacks and cancellation for one streaming `entity_invoke`. */
export interface StreamCallbacks {
  onToken: (text: string) => void;
  onThinking?: (text: string) => void;
  signal?: AbortSignal;
}

const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * A stream fails when it goes this long with NO sign of life — no token, no
 * thinking, no host keepalive. An idle bound rather than a total one: a
 * reasoning model can legitimately take minutes, and a fixed total cut long
 * answers off mid-reply while the platform finished (and billed) them anyway.
 */
export const STREAM_IDLE_TIMEOUT_MS = 120_000;

/** Absolute ceiling on one stream, however lively. */
export const STREAM_MAX_DURATION_MS = 15 * 60_000;

/**
 * Low-level bridge client for postMessage communication with the FIAS host frame.
 * Used internally by SDK hooks. Plugin developers should use hooks instead.
 */
export class FiasBridge {
  private pendingRequests = new Map<string, PendingRequest>();
  private initialized = false;
  private initPromise: Promise<void> | null = null;
  private initResolve: (() => void) | null = null;
  private archId: string = '';
  private permissions: PluginPermission[] = [];
  private theme: FiasTheme | null = null;
  private vendoredAssets: FiasVendoredAssets | null = null;
  private textModels: FiasTextModel[] | null = null;
  private textModelListeners = new Set<(models: FiasTextModel[]) => void>();
  private holder: FiasHolder | null = null;
  private holderListeners = new Set<(holder: FiasHolder | null) => void>();
  private currentPath: string = '';
  // Host-chrome state (useFiasChrome). `null` until init, and from a host
  // older than the chrome lane — `hasChrome` tells the two apart.
  private chrome: FiasChromeState | null = null;
  private hasChrome = false;
  private chromeListeners = new Set<(chrome: FiasChromeState | null) => void>();
  private hostOrigin: string = '';
  private themeListeners = new Set<(theme: FiasTheme) => void>();
  private accessListeners = new Set<() => void>();
  private navigationListeners = new Set<(path: string) => void>();
  private stepNavigationListeners = new Set<(nodeId: string) => void>();
  // Preview-state preservation (preview mode only). Keyed so multiple
  // useFiasPreviewState() callers don't collide. Providers produce a snapshot
  // on `preview_state_capture`; restore listeners receive it after the reload.
  private previewStateProviders = new Map<string, () => unknown>();

  /**
   * The plugin's handler for host-dispatched Fias AI actions (one per plugin
   * — `useFiasAIActions` owns registration). Throw/reject to report failure;
   * the assistant awaits the result before telling the model it applied.
   */
  private aiActionHandler:
    | ((actionId: string, params: Record<string, unknown>) => void | Promise<unknown>)
    | null = null;
  private handoffHandler: ((payload: ArcheHandoffPayload) => void) | null = null;
  // A handoff that arrived before `useArcheHandoff` registered. The host sends
  // as soon as the iframe reports ready, which is reliably BEFORE the React
  // effect that registers the handler runs — without this the very first
  // "open this file in <app>" of a session would be dropped, which is the one
  // the user actually asked for. Holds the LATEST only: a second handoff
  // supersedes an unconsumed first (the user changed their mind mid-open).
  private pendingHandoff: ArcheHandoffPayload | null = null;
  private previewStateRestoreListeners = new Map<string, (state: unknown) => void>();
  // A restore payload that arrived before its hook registered (the host
  // restores on `ready`, but the React effect that registers runs later).
  // Consumed per-key as listeners register.
  private bufferedRestoreState: Record<string, unknown> | null = null;
  // Realtime subscriptions (ADR-012 Phase M4), keyed by the HOST-MINTED
  // logical subscriptionId returned from a subscribe-kind entity_invoke.
  // The plugin never sees grants/topics — the host manages the transport;
  // these listeners receive typed invalidations and terminal endings.
  private subscriptionEventListeners = new Map<string, Set<(event: SubscriptionEvent) => void>>();
  private subscriptionEndedListeners = new Map<
    string,
    Set<(reason: SubscriptionEndedReason) => void>
  >();
  private listening = false;

  constructor() {
    this.initPromise = new Promise((resolve) => {
      this.initResolve = resolve;
    });

    this.attach();
  }

  /**
   * Register the message listener. Safe to call multiple times —
   * will not add duplicate listeners.
   *
   * Sends a `request_init` message to the host after attaching. This
   * handles the srcDoc race condition where the host sends `init` before
   * our module has loaded and our listener is attached.
   */
  attach(): void {
    if (typeof window !== 'undefined' && !this.listening) {
      window.addEventListener('message', this.handleMessage);
      this.listening = true;

      // Ask the host to (re-)send init in case we missed the first one
      if (window.parent !== window) {
        window.parent.postMessage({ type: 'request_init' }, '*');
      }
    }
  }

  /**
   * Wait for the host to send the init message.
   */
  async waitForInit(): Promise<void> {
    if (this.initialized) return;
    return this.initPromise!;
  }

  /**
   * Signal to the host that the plugin is loaded and ready. Announces
   * the SDK's bridge protocol version so the host can detect (and surface)
   * an out-of-date plugin before it tries to call ops the host removed.
   */
  ready(): void {
    this.send('ready', {
      sdkProtocolVersion: SDK_BRIDGE_PROTOCOL_VERSION,
      // Advertise preview-state support so the host can snapshot/restore us
      // across a preview rebuild. Older SDKs omit this and the host skips it.
      supportsPreviewState: true,
      // Advertise that this SDK understands `arche_handoff`. `ready` alone
      // cannot tell the host that — every older SDK sends `ready` too, and
      // then ignores an unknown message type. Since the handoff lane is
      // one-way and unacknowledged, without this flag a handoff to an older
      // bundle is dropped in silence: the user asks to open a file somewhere
      // and simply nothing happens, with nothing for the host to report.
      //
      // Statically true because the capability belongs to the SDK VERSION, not
      // to whether a hook happens to be mounted right now — a plugin that
      // registers `useArcheHandoff` a tick later is covered by the buffer in
      // `setHandoffHandler`. Older SDKs omit it and the host refuses to
      // dispatch, exactly like `supportsPreviewState`.
      supportsArcheHandoff: true,
    });
  }

  /**
   * Request the host to resize the plugin iframe.
   */
  resize(height: number): void {
    this.send('resize', { height });
  }

  /**
   * Show a platform toast notification.
   */
  showToast(message: string, variant: 'info' | 'success' | 'warning' | 'error' = 'info'): void {
    this.send('toast', { message, variant });
  }

  /**
   * Send a request to the host and wait for a response.
   */
  async request<T>(
    type: PluginToHostMessageType,
    payload: unknown,
    timeoutMs?: number,
  ): Promise<T> {
    await this.waitForInit();

    return new Promise<T>((resolve, reject) => {
      const messageId = generateMessageId();
      const timer = setTimeout(() => {
        this.pendingRequests.delete(messageId);
        reject(new FiasBridgeError(`Bridge request timed out: ${type}`, 'TIMEOUT'));
      }, timeoutMs ?? DEFAULT_TIMEOUT_MS);

      this.pendingRequests.set(messageId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer,
      });

      this.send(type, payload, messageId);
    });
  }

  getArchId(): string {
    return this.archId;
  }

  getPermissions(): PluginPermission[] {
    return [...this.permissions];
  }

  getTheme(): FiasTheme | null {
    return this.theme;
  }

  /**
   * Asset URLs for a platform-vendored library (init payload), or null when the
   * host's CDN is unconfigured, the plugin lacks `sandbox:vendored-libraries`,
   * or the library isn't vendored. Static per session — no update listener
   * needed. `importSpecifier` is the library's bare module name (e.g.
   * `'pdfjs-dist'`). See `useFiasVendoredAssets`.
   */
  getVendoredAssets(importSpecifier: string): Record<string, string> | null {
    return this.vendoredAssets?.[importSpecifier] ?? null;
  }

  getCurrentPath(): string {
    return this.currentPath;
  }

  /**
   * The host-chrome state, or null before init / from a host without the
   * chrome lane (`hasChromeSupport` tells the two apart). See `useFiasChrome`.
   */
  getChrome(): FiasChromeState | null {
    return this.chrome;
  }

  /** True once a host has delivered chrome state (init or chrome_update). */
  hasChromeSupport(): boolean {
    return this.hasChrome;
  }

  /** Called on init and on every chrome_update. */
  onChromeUpdate(listener: (chrome: FiasChromeState | null) => void): () => void {
    this.chromeListeners.add(listener);
    return () => this.chromeListeners.delete(listener);
  }

  /**
   * Set host-chrome state for this arche (fire-and-forget; the host echoes the
   * result as chrome_update). Every present key must be a boolean — the host
   * drops the whole message otherwise. See `useFiasChrome`.
   */
  setChrome(patch: FiasChromeSetPayload): void {
    this.send('chrome_set', patch);
  }

  /**
   * Leave the arche for a host destination — a closed enum, never a URL.
   * See `useFiasChrome`.
   */
  navigateHost(target: FiasChromeNavigateTarget): void {
    this.send('chrome_navigate', { target });
  }

  /**
   * The live text-model catalog, or null until the host has delivered it (or
   * when the plugin lacks `entities:invoke`). See `useTextModels`.
   */
  getTextModels(): FiasTextModel[] | null {
    return this.textModels;
  }

  /**
   * The workspace the plugin was opened in, or null before init, without
   * `user:profile:read` / `data:workspace`, or from an older host. Fixed for
   * the life of the plugin. See `useFiasHolder`.
   */
  getHolder(): FiasHolder | null {
    return this.holder;
  }

  /** Called once, when init delivers the holder. */
  onHolderReady(listener: (holder: FiasHolder | null) => void): () => void {
    this.holderListeners.add(listener);
    return () => this.holderListeners.delete(listener);
  }

  onTextModelsUpdate(listener: (models: FiasTextModel[]) => void): () => void {
    this.textModelListeners.add(listener);
    return () => this.textModelListeners.delete(listener);
  }

  onThemeUpdate(listener: (theme: FiasTheme) => void): () => void {
    this.themeListeners.add(listener);
    return () => this.themeListeners.delete(listener);
  }

  /**
   * The host says the person's own access may have changed (ADR-034) — after
   * its access sheet changed it. Carries nothing: ask `my_access` again.
   */
  onAccessUpdate(listener: () => void): () => void {
    this.accessListeners.add(listener);
    return () => this.accessListeners.delete(listener);
  }

  onNavigationUpdate(listener: (path: string) => void): () => void {
    this.navigationListeners.add(listener);
    return () => this.navigationListeners.delete(listener);
  }

  onStepNavigationUpdate(listener: (nodeId: string) => void): () => void {
    this.stepNavigationListeners.add(listener);
    return () => this.stepNavigationListeners.delete(listener);
  }

  /**
   * Opt into preview-state preservation. In the builder preview, an in-place
   * rebuild reloads the iframe and destroys the plugin's in-memory state (e.g.
   * a generated report). Registering here lets the host snapshot `getState()`
   * before the reload and hand it back to `onRestore` afterwards.
   *
   * `key` namespaces the snapshot so independent callers don't collide. Outside
   * preview mode the host never requests a capture, so this is inert. Returns an
   * unregister function.
   */
  registerPreviewState(
    key: string,
    getState: () => unknown,
    onRestore: (state: unknown) => void,
  ): () => void {
    this.previewStateProviders.set(key, getState);
    this.previewStateRestoreListeners.set(key, onRestore);
    // A restore may have arrived before this registration — deliver it now.
    this.deliverBufferedRestore();
    return () => {
      this.previewStateProviders.delete(key);
      this.previewStateRestoreListeners.delete(key);
    };
  }

  /** Deliver any buffered restore payload to registered listeners (once per key). */
  private deliverBufferedRestore(): void {
    if (!this.bufferedRestoreState) return;
    for (const [key, listener] of this.previewStateRestoreListeners) {
      if (key in this.bufferedRestoreState) {
        try {
          listener(this.bufferedRestoreState[key]);
        } catch {
          // A faulty restore handler must not break the bridge.
        }
        delete this.bufferedRestoreState[key];
      }
    }
    if (Object.keys(this.bufferedRestoreState).length === 0) {
      this.bufferedRestoreState = null;
    }
  }

  /**
   * Send a streaming entity_invoke request. Tokens arrive via stream_token
   * messages and are forwarded to the onToken callback. The returned promise
   * resolves with the final EntityInvocationResult when the host sends the
   * normal response message.
   */
  async requestStream(
    payload: unknown,
    callbacks: StreamCallbacks | ((text: string) => void),
  ): Promise<EntityInvocationResult> {
    const { onToken, onThinking, signal } =
      typeof callbacks === 'function' ? { onToken: callbacks } : callbacks;
    await this.waitForInit();

    // Refuse here, by name, a call the bridge would reject with a bare 413.
    const outbound = { ...(payload as object), stream: true } as Record<string, unknown>;
    const size = checkInvocationRequestSize(outbound);
    if (!size.ok) {
      throw new FiasBridgeError(size.message, 'REQUEST_TOO_LARGE');
    }

    return new Promise<EntityInvocationResult>((resolve, reject) => {
      if (signal?.aborted) {
        reject(new FiasBridgeError('Stream aborted before it started', 'ABORTED'));
        return;
      }
      const messageId = generateMessageId();
      const fail = (error: FiasBridgeError) => {
        const pending = this.pendingRequests.get(messageId);
        if (!pending) return;
        clearTimeout(pending.timer);
        pending.cleanup?.();
        this.pendingRequests.delete(messageId);
        reject(error);
      };
      const idleTimer = () =>
        setTimeout(
          () =>
            fail(
              new FiasBridgeError('Bridge streaming request timed out: entity_invoke', 'TIMEOUT'),
            ),
          STREAM_IDLE_TIMEOUT_MS,
        );
      const maxTimer = setTimeout(
        () =>
          fail(
            new FiasBridgeError(
              'Bridge streaming request exceeded its maximum duration: entity_invoke',
              'TIMEOUT',
            ),
          ),
        STREAM_MAX_DURATION_MS,
      );
      // Abort stops waiting; the platform still completes and bills the call.
      const onAbort = () => fail(new FiasBridgeError('Stream aborted', 'ABORTED'));
      signal?.addEventListener('abort', onAbort, { once: true });

      const pending: PendingRequest = {
        resolve: resolve as (value: unknown) => void,
        reject,
        timer: idleTimer(),
        onToken,
        onThinking,
        onActivity: () => {
          clearTimeout(pending.timer);
          pending.timer = idleTimer();
        },
        cleanup: () => {
          clearTimeout(maxTimer);
          signal?.removeEventListener('abort', onAbort);
        },
      };
      this.pendingRequests.set(messageId, pending);

      this.send('entity_invoke', outbound, messageId);
    });
  }

  /**
   * Clean up event listeners and pending requests.
   * After destroy(), the bridge can be re-activated by calling attach().
   */
  destroy(): void {
    if (typeof window !== 'undefined' && this.listening) {
      window.removeEventListener('message', this.handleMessage);
      this.listening = false;
    }
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timer);
      pending.cleanup?.();
      pending.reject(new FiasBridgeError('Bridge destroyed', 'BRIDGE_DESTROYED'));
    }
    this.pendingRequests.clear();
    this.themeListeners.clear();
    this.accessListeners.clear();
    this.textModelListeners.clear();
    this.holderListeners.clear();
    this.navigationListeners.clear();
    this.stepNavigationListeners.clear();
    this.previewStateProviders.clear();
    this.previewStateRestoreListeners.clear();
    this.bufferedRestoreState = null;
    this.subscriptionEventListeners.clear();
    this.subscriptionEndedListeners.clear();
  }

  /**
   * Listens for invalidation events on a logical realtime subscription
   * (the id returned by a subscribe-kind entity_invoke). Returns an
   * unsubscribe function for the LISTENER only — releasing the
   * subscription itself is `releaseSubscription`.
   */
  onSubscriptionEvent(
    subscriptionId: string,
    listener: (event: SubscriptionEvent) => void,
  ): () => void {
    let set = this.subscriptionEventListeners.get(subscriptionId);
    if (!set) {
      set = new Set();
      this.subscriptionEventListeners.set(subscriptionId, set);
    }
    set.add(listener);
    return () => {
      const listeners = this.subscriptionEventListeners.get(subscriptionId);
      listeners?.delete(listener);
      if (listeners && listeners.size === 0) {
        this.subscriptionEventListeners.delete(subscriptionId);
      }
    };
  }

  /**
   * Listens for the terminal ending of a logical realtime subscription.
   * Reasons are a closed union — `authorization_revoked` and
   * `collection_deleted` (the resource is gone) are definitive; the rest may be worth a fresh
   * subscribe once conditions change.
   */
  onSubscriptionEnded(
    subscriptionId: string,
    listener: (reason: SubscriptionEndedReason) => void,
  ): () => void {
    let set = this.subscriptionEndedListeners.get(subscriptionId);
    if (!set) {
      set = new Set();
      this.subscriptionEndedListeners.set(subscriptionId, set);
    }
    set.add(listener);
    return () => {
      const listeners = this.subscriptionEndedListeners.get(subscriptionId);
      listeners?.delete(listener);
      if (listeners && listeners.size === 0) {
        this.subscriptionEndedListeners.delete(subscriptionId);
      }
    };
  }

  /**
   * Releases one logical realtime subscription (fire-and-forget). The host
   * refcounts: the underlying socket topic is torn down when the last
   * logical subscriber releases.
   */
  releaseSubscription(subscriptionId: string): void {
    this.subscriptionEventListeners.delete(subscriptionId);
    this.subscriptionEndedListeners.delete(subscriptionId);
    this.send('subscription_unsubscribe', { subscriptionId });
  }

  private handleMessage = (event: MessageEvent): void => {
    const data = event.data;
    if (!data || typeof data !== 'object' || !data.type) return;

    // Handle init message from host (only process once to prevent replay).
    // The first init message establishes the trusted host origin.
    if (data.type === 'init') {
      if (this.initialized) return;
      const initMsg = data as BridgeInitMessage;

      // Protocol version check: refuse to initialize against a host that
      // speaks an older bridge protocol than this SDK requires. Absence of
      // `hostProtocolVersion` is treated as version 1 (the first published
      // contract — older hosts that don't include it are still v1-compatible).
      // See `protocol.ts` for the versioning policy.
      const hostVersion = initMsg.payload.hostProtocolVersion ?? 1;
      if (hostVersion < SDK_REQUIRES_HOST_PROTOCOL_VERSION) {
        // Don't pin hostOrigin or mark initialized — leave the bridge in
        // its pre-init state so the plugin's `waitForInit()` never
        // resolves. The plugin author sees a clear console error rather
        // than running against a host that's missing capabilities the
        // SDK relies on.
        console.error(
          `[fias-sdk] Host bridge protocol v${hostVersion} is below the minimum this SDK supports (v${SDK_REQUIRES_HOST_PROTOCOL_VERSION}). Plugin refused to initialize.`,
        );
        return;
      }

      this.hostOrigin = event.origin;
      this.archId = initMsg.payload.archId;
      this.permissions = initMsg.payload.permissions;
      this.theme = mergeThemeWithDefaults(initMsg.payload.theme);
      this.vendoredAssets = initMsg.payload.vendoredAssets ?? null;
      this.textModels = initMsg.payload.textModels ?? null;
      this.holder = initMsg.payload.holder ?? null;
      this.currentPath = initMsg.payload.currentPath;
      // `chrome` is a key an older host never sends: its absence (not its
      // null) is what marks the lane unsupported.
      this.hasChrome = 'chrome' in initMsg.payload;
      this.chrome = initMsg.payload.chrome ?? null;
      this.initialized = true;
      this.initResolve?.();
      // Notify listeners so hooks that subscribed before init arrived
      // (the common case — React's useEffect fires on commit, and init
      // often hasn't landed yet) pick up the initial path. Without this,
      // useFiasNavigation stays at its pre-init `''` until the first
      // navigate_update, which for direct-URL entries (e.g. shared links)
      // never comes — the plugin renders its home page instead of the
      // sub-path.
      for (const listener of this.navigationListeners) {
        listener(this.currentPath);
      }
      for (const listener of this.holderListeners) {
        listener(this.holder);
      }
      for (const listener of this.chromeListeners) {
        listener(this.chrome);
      }
      return;
    }

    // After init, only accept messages from the verified host origin
    if (this.hostOrigin && event.origin !== this.hostOrigin) return;

    // Handle theme updates
    if (data.type === 'theme_update') {
      this.theme = mergeThemeWithDefaults(data.payload as Partial<FiasTheme>);
      for (const listener of this.themeListeners) {
        listener(this.theme);
      }
      return;
    }

    // The person's own access may have changed (ADR-034).
    if (data.type === 'access_update') {
      for (const listener of this.accessListeners) listener();
      return;
    }

    // The text-model catalog, when the host loaded it after init.
    if (data.type === 'text_models_update') {
      if (Array.isArray(data.payload)) {
        this.textModels = data.payload as FiasTextModel[];
        for (const listener of this.textModelListeners) {
          listener(this.textModels);
        }
      }
      return;
    }

    // Handle navigation updates
    if (data.type === 'navigate_update') {
      this.currentPath = data.payload as string;
      for (const listener of this.navigationListeners) {
        listener(this.currentPath);
      }
      return;
    }

    // Host-chrome state after any change — the plugin's own chrome_set echoed
    // back, or the user clicking the host's controls (useFiasChrome).
    if (data.type === 'chrome_update') {
      if (data.payload && typeof data.payload === 'object') {
        this.hasChrome = true;
        this.chrome = data.payload as FiasChromeState;
        for (const listener of this.chromeListeners) {
          listener(this.chrome);
        }
      }
      return;
    }

    // Handle step navigation from host
    if (data.type === 'step_navigate') {
      const { nodeId } = data.payload as { nodeId: string };
      if (typeof nodeId === 'string') {
        for (const listener of this.stepNavigationListeners) {
          listener(nodeId);
        }
      }
      return;
    }

    // Host requests a snapshot of the plugin's transient state before a preview
    // rebuild. Reply with a keyed snapshot (empty if nothing registered) so the
    // host's capture resolves promptly either way.
    if (data.type === 'preview_state_capture') {
      const snapshot: Record<string, unknown> = {};
      for (const [key, provider] of this.previewStateProviders) {
        try {
          // Round-trip each slice so a non-serializable value (function,
          // circular ref, DOM node) is dropped here rather than throwing out of
          // postMessage's structured clone and breaking the whole capture.
          snapshot[key] = JSON.parse(JSON.stringify(provider()));
        } catch {
          // Skip a provider that throws or returns non-JSON-safe state.
        }
      }
      this.send('preview_state', snapshot);
      return;
    }

    // Host replays a previously-captured snapshot after the reload. Buffer it so
    // hooks that register slightly later (their effect runs after init) still
    // receive their slice.
    if (data.type === 'preview_state_restore') {
      const state = (data.payload as Record<string, unknown>) ?? {};
      this.bufferedRestoreState = { ...(this.bufferedRestoreState ?? {}), ...state };
      this.deliverBufferedRestore();
      return;
    }

    // Handle streaming tokens
    if (data.type === 'stream_token') {
      const { messageId, text, channel } = data as {
        type: string;
        messageId: string;
        text?: string;
        channel?: 'text' | 'thinking' | 'keepalive';
      };
      const pending = this.pendingRequests.get(messageId);
      if (!pending) return;
      pending.onActivity?.();
      if (typeof text !== 'string' || channel === 'keepalive') return;
      if (channel === 'thinking') {
        pending.onThinking?.(text);
      } else {
        pending.onToken?.(text);
      }
      return;
    }

    // Fias AI action dispatch: the assistant asked the host to perform one of
    // this plugin's declared actions. Run the registered handler and reply
    // `ai_action_result` with the SAME messageId — the host's pending map is
    // keyed by it. Always reply (unknown handler = failure): a silent drop
    // would leave the assistant's paused turn to ride out its timeout.
    if (data.type === 'ai_action_dispatch') {
      const { messageId } = data as BridgeMessage;
      const payload = (data as BridgeMessage).payload as
        | { actionId?: string; params?: Record<string, unknown> }
        | undefined;
      const handler = this.aiActionHandler;
      void (async () => {
        try {
          if (typeof payload?.actionId !== 'string') {
            throw new Error('malformed dispatch: missing actionId');
          }
          if (!handler) {
            throw new Error('the plugin has no Fias AI action handler registered');
          }
          await handler(payload.actionId, payload.params ?? {});
          this.send('ai_action_result', { ok: true }, messageId);
        } catch (err) {
          this.send(
            'ai_action_result',
            { ok: false, error: err instanceof Error ? err.message : String(err) },
            messageId,
          );
        }
      })();
      return;
    }

    // Arche Handoff: the host is handing this plugin a document to open (the
    // `open_document` payload — see docs/arche-interop.md). One-way: the
    // plugin owns the outcome, including refusing a file type it can't open,
    // and reports that to the user in its own UI. There is no result message,
    // so a plugin that never registers a handler simply ignores it.
    if (data.type === 'arche_handoff') {
      const payload = (data as BridgeMessage).payload as ArcheHandoffPayload | undefined;
      if (payload && typeof payload === 'object' && typeof payload.kind === 'string') {
        if (this.handoffHandler) {
          try {
            this.handoffHandler(payload);
          } catch {
            // A throwing plugin handler must not take down the bridge's
            // message loop; the plugin's own error surfaces in its console.
          }
        } else {
          this.pendingHandoff = payload;
        }
      }
      return;
    }

    // Realtime subscription invalidation (ADR-012 Phase M4): notify-then-
    // refetch — the event only says the observed resource changed.
    if (data.type === 'subscription_event') {
      const payload = data.payload as {
        subscriptionId?: string;
        event?: SubscriptionEvent;
      };
      if (typeof payload?.subscriptionId === 'string' && payload.event) {
        const listeners = this.subscriptionEventListeners.get(payload.subscriptionId);
        if (listeners) {
          for (const listener of listeners) listener(payload.event);
        }
      }
      return;
    }

    // Terminal ending of a realtime subscription (host-side lease/authz).
    if (data.type === 'subscription_ended') {
      const payload = data.payload as {
        subscriptionId?: string;
        reason?: SubscriptionEndedReason;
      };
      if (typeof payload?.subscriptionId === 'string' && payload.reason) {
        const listeners = this.subscriptionEndedListeners.get(payload.subscriptionId);
        if (listeners) {
          for (const listener of listeners) listener(payload.reason);
        }
        this.subscriptionEventListeners.delete(payload.subscriptionId);
        this.subscriptionEndedListeners.delete(payload.subscriptionId);
      }
      return;
    }

    // Handle response messages
    if (data.type === 'response') {
      const response = data as BridgeResponse;
      const pending = this.pendingRequests.get(response.messageId);
      if (!pending) return;

      clearTimeout(pending.timer);
      pending.cleanup?.();
      this.pendingRequests.delete(response.messageId);

      if (response.error) {
        // The wire format is plain fields (string + optional code); the Error
        // instance is constructed HERE, never structured-cloned across the
        // postMessage boundary.
        pending.reject(
          new FiasBridgeError(
            response.error || 'Bridge request failed',
            response.errorCode ?? 'BRIDGE_ERROR',
            response.errorRequestId,
          ),
        );
      } else {
        pending.resolve(response.payload);
      }
    }
  };

  /**
   * Register (or clear, with null) THE handler for host-dispatched Fias AI
   * actions. One per plugin — `useFiasAIActions` owns this; last write wins.
   */
  setAiActionHandler(
    handler:
      | ((actionId: string, params: Record<string, unknown>) => void | Promise<unknown>)
      | null,
  ): void {
    this.aiActionHandler = handler;
  }

  /**
   * Register (or clear, with null) the handler for an incoming Arche Handoff.
   * Registering drains a handoff that arrived before the hook mounted, so the
   * first one of a session is never lost. Use `useArcheHandoff` rather than
   * calling this directly.
   */
  clearHandoffHandler(handler: (payload: ArcheHandoffPayload) => void): void {
    // Identity-checked: the slot holds one handler, so an unconditional clear
    // would let a component that unmounts LATER wipe a registration made by one
    // that mounted after it — leaving the plugin silently deaf to every
    // subsequent handoff, on a lane that is one-way and un-acknowledged and so
    // reports nothing when it happens.
    if (this.handoffHandler === handler) this.handoffHandler = null;
  }

  setHandoffHandler(handler: ((payload: ArcheHandoffPayload) => void) | null): void {
    this.handoffHandler = handler;
    if (handler && this.pendingHandoff) {
      const buffered = this.pendingHandoff;
      this.pendingHandoff = null;
      try {
        handler(buffered);
      } catch {
        // See the inbound branch — a plugin handler's throw stays contained.
      }
    }
  }

  /**
   * Declare (or clear, with null) the plugin's runtime Fias AI actions.
   * Fire-and-forget; the host validates, bounds, and throttles (~1/s), and
   * honours the runtime lane only for TRUSTED arches — non-trusted plugins
   * declare statically via the manifest `fiasAI` block. Requires the
   * `ai:actions` permission.
   */
  declareAiActions(declaration: FiasAIActionsDeclaration | null): void {
    this.send('ai_actions_declare', declaration);
  }

  /**
   * Push the state snapshot the assistant sees (and `availableWhen` filters
   * against). Keep it SMALL and flag-shaped ({documentOpen: true, …}): the
   * host drops snapshots over its byte cap, and accepted ones are embedded
   * in the assistant's system prompt every turn.
   */
  sendAiState(state: Record<string, unknown>): void {
    this.send('ai_state', state);
  }

  /**
   * Ask the host to open the global Fias AI panel (fire-and-forget). Use it
   * from a CONTEXTUAL affordance (an empty-state "Ask Fias AI" button, a help
   * menu item) — the host also shows its own "Works with Fias AI" chip, so
   * don't build a permanent duplicate. The host debounces (~1 per 10s), and
   * ignores requests for a while after the user closes the panel — never
   * loop or retry. There is deliberately NO way to pass text along: the
   * assistant's input belongs to the user.
   */
  openAssistant(): void {
    this.send('ai_open', {});
  }

  private send(type: PluginToHostMessageType, payload: unknown, messageId?: string): void {
    if (typeof window === 'undefined') return;

    const message: BridgeMessage = {
      type,
      messageId: messageId ?? generateMessageId(),
      payload,
    };

    // Use the verified host origin when available; fall back to '*' only
    // for the initial 'ready' message sent before init is received.
    const targetOrigin = this.hostOrigin || '*';
    window.parent.postMessage(message, targetOrigin);
  }
}

/**
 * Singleton bridge instance. Eagerly created at module scope so the
 * message listener is registered as early as possible. When loading via
 * srcDoc, the host may send init before this module executes — the bridge
 * sends a `request_init` message to the host on attach to handle this.
 *
 * The dialog shims ride along here rather than in `index.ts` because this is
 * the module every plugin reaches no matter which hook it imports, and a
 * top-level side effect in the package entry is the kind of thing a bundler
 * may drop. See sandbox-dialogs.ts for why they exist at all.
 */
if (typeof window !== 'undefined') installSandboxDialogShims();

let bridgeInstance: FiasBridge | null = typeof window !== 'undefined' ? new FiasBridge() : null;

export function getBridge(): FiasBridge {
  if (!bridgeInstance) {
    bridgeInstance = new FiasBridge();
  }
  return bridgeInstance;
}

/**
 * Reset the bridge singleton (used in testing).
 */
export function resetBridge(): void {
  bridgeInstance?.destroy();
  bridgeInstance = null;
}
